import { Box, Tab, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PlanOverviewTabItem, PlanOverviewTabProps } from '../../../common/types/plan-overview-entity';
import { StyledTabs, TabsWrapper } from '../../../themes/styles/plan-overview';
import { InfoTooltip } from '../InfoTooltip';
import MappingGuideLink from '../../../containers/goals-inline-flow2/MappingGuideLink';

const CountBadge = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'ftds'
})<{ ftds?: boolean }>(({ theme, ftds }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '22px',
    fontSize: '12px',
    lineHeight: 1,
    ...(ftds
        ? {
              height: '20px',
              borderRadius: '999px',
              border: `1px solid ${theme.palette.primary[800]}`,
              backgroundColor: 'transparent',
              color: theme.palette.primary[800],
              fontWeight: 400,
              padding: '2px 8px',
              marginLeft: '4px'
          }
        : {
              height: '22px',
              borderRadius: '50%',
              backgroundColor: theme.palette.info[400],
              color: theme.palette.common.white,
              fontWeight: 600,
              padding: '0 4px',
              marginLeft: '6px'
          })
}));

const hasTooltip = (item: PlanOverviewTabItem) => Boolean(item.tooltipKey || item.tooltipMessage);

export const PrimaryTabs = ({
    tabItems,
    value,
    isPlanDetailsEnhancementEnabled,
    ftdsBadge,
    onChange,
    showMappingGuideLink
}: PlanOverviewTabProps) => {
    const theme = useTheme();
    if (!tabItems?.length) {
        return null;
    }

    const visibleItems = tabItems.map((item, index) => ({ item, index })).filter(({ item }) => !item.hidden);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        const target = tabItems[newValue];
        if (target?.disabled) return;
        onChange(event, newValue);
    };

    return (
        <TabsWrapper>
            <StyledTabs
                value={value}
                onChange={handleChange}
                isPlanDetailsEnhancementEnabled={isPlanDetailsEnhancementEnabled}
                ftds={ftdsBadge}
            >
                {visibleItems.map(({ item, index }) => {
                    const isDisabled = Boolean(item.disabled);
                    const itemHasTooltip = hasTooltip(item);

                    const labelContent = (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {item.label}
                            {item.count != null && item.count > 0 && (
                                <CountBadge ftds={ftdsBadge}>{item.count}</CountBadge>
                            )}
                        </Box>
                    );

                    const labelNode = itemHasTooltip ? (
                        <InfoTooltip
                            placement={item.tooltipPlacement ?? 'bottom'}
                            width={item.tooltipWidth ?? 380}
                            t={item.tooltipKey}
                            message={item.tooltipMessage}
                            messageVariant="labels.small"
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                                ...(isDisabled ? { gap: '0.5rem' } : {})
                            }}
                            showInfoIcon={isDisabled}
                            showInfoOnClick={isDisabled}
                        >
                            {labelContent}
                        </InfoTooltip>
                    ) : (
                        labelContent
                    );

                    // Note: we intentionally do NOT pass MUI's `disabled` prop because it sets
                    // `pointer-events: none`, which would prevent the InfoTooltip hover from firing.
                    // Instead we apply disabled styling + block selection in handleChange, and surface
                    // disabled state to assistive tech via aria-disabled.
                    const sx = {
                        ...(itemHasTooltip && { padding: 0 }),
                        ...(isDisabled && {
                            color: `${theme.palette.semantic.text.mutedLight} !important`,
                            '&:hover': { color: 'inherit' }
                        })
                    };

                    return (
                        <Tab
                            key={`goal-tab-${index}`}
                            value={index}
                            disableRipple={isDisabled}
                            aria-disabled={isDisabled || undefined}
                            tabIndex={isDisabled ? -1 : undefined}
                            sx={Object.keys(sx).length ? sx : undefined}
                            label={labelNode}
                        />
                    );
                })}
                {showMappingGuideLink && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <MappingGuideLink />
                    </Box>
                )}
            </StyledTabs>
        </TabsWrapper>
    );
};
